// 🔧 use_event_doc.js
// ✅ Purpose: Provide a hook to load event metadata (eventDoc)
// ✅ Features:
// - Reads directly from events/{eventId}
// - No localStorage caching
// - Returns fresh Firestore data
// - Handles loading state
// - Safe for public and admin pages
// - Fully commented for clarity

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import { logger } from "../utils/logger";

export const useEventDoc = (eventId) => {
  const [eventDoc, setEventDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ✅ Reset state when event changes
    setEventDoc(null);

    if (!eventId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadEventDoc = async () => {
      setLoading(true);

      try {
        const ref = doc(db, "events", eventId);
        const snap = await getDoc(ref);

        if (cancelled) return;

        if (snap.exists()) {
          setEventDoc({
            id: snap.id,
            ...snap.data(),
          });

          logger.log("✅ Event document loaded:", {
            eventId,
          });
        } else {
          logger.warn("⚠️ Event document not found:", {
            eventId,
          });
        }
      } catch (err) {
        if (cancelled) return;

        logger.error("❌ Failed to load event document:", {
          value: err,
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadEventDoc();

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return {
    eventDoc,
    loading,
  };
};